<script setup>
import AbsenceDocumentAttachment from '@/components/shared/AbsenceDocumentAttachment.vue';
import SharedDeleteConfirmDialog from '@/components/shared/SharedDeleteConfirmDialog.vue';
import AbsenceDocumentPicker from '@/components/shared/AbsenceDocumentPicker.vue';
import notification from "@/utils/plugins/NotificationService";
import { computed, h, onMounted, onUnmounted, ref, shallowRef } from 'vue';
import AbsenceService from '@/api/services/absence.service';
import AbsenceSupportingDocument from '@/api/services/absenceSupportingDocument.service';
import EmployeeService from '@/api/services/employee.service';
import ResourceService from '@/api/services/resource.service';

import { filterByNormalizedText } from "@/utils/textSearch";
import { helpers, required } from "@vuelidate/validators";
import dayjs from "dayjs";
import useVuelidate from '@vuelidate/core';
import { useUiStore } from "@/app/stores/uiStore";


const uiStore = useUiStore();
const saving = ref(false);
//const router = useRouter();

// eslint-disable-next-line no-unused-vars
const absence = ref({ employee: { name: '', lastName: '' } });
const absenceTypes = ref([]);
const totalResults = ref(0);
const params = ref({});
const employees = ref([]);
const employeeSearchQuery = ref('');
const deleteDialogShown = ref(false);
const selectedAbsence = ref(null);
const showModalNewAbsence = ref(false);
const newFileList = ref([]);
const pendingFiles = ref([]);
const closeWarningShown = ref(false);
const initialSnapshot = ref(null);
const fileList = ref([]);
const documentRemoval = ref(null);
const documentDeleteShown = ref(false);

const now = new Date();
const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 0);
const fechaDesde = ref(startOfDay)
const fechaHasta = ref(endOfDay)

const absenceFormModel = ref({
  id: null,
  employee: null,
  employeeName: '',
  fromDate: startOfDay,
  toDate: endOfDay,
  absenceType: null,
  observations: null,
  absenceSupportingDocuments: []
});
const customPrefix = shallowRef({
  render() {
    return h('img', {
      src: '/calendar-blue.svg',
      style: 'width: 16px; height: 16px; margin-right: 10px;',
    });
  },
})

// eslint-disable-next-line no-undef
const props = defineProps({
  absenceModal: Object,
});

// eslint-disable-next-line no-undef
const emits = defineEmits(['closeDialog', 'changeAbsence', 'savingChange']);

const rules = computed(() => {
  return {
    employeeName: {
      required: helpers.withMessage("Este campo es obligatorio", required),
    },
    fromDate: {
      required: helpers.withMessage("Este campo es obligatorio", required),
    },
    toDate: {
      required: helpers.withMessage("Este campo es obligatorio", required),
    },
    absenceType: {
      required: helpers.withMessage("Este campo es obligatorio", required),
    },
    observations: {
      required: helpers.withMessage("Este campo es obligatorio", required),
    },
  };
});

const totalTime = computed(() => {
  return formatAbsentTime(absenceFormModel.value.absentWorkTime);
})
const filteredEmployees = computed(() => {
  return filterByNormalizedText(employees.value, employeeSearchQuery.value, employee => employee.fullName);
})

const v$ = useVuelidate(rules, absenceFormModel);
const v = v$.value;





onMounted(() => {
  getEmployees();
  getAbsenceTypes();

  if (props.absenceModal) {
    if (props.absenceModal.id) {
      const absence = props.absenceModal;
      absenceFormModel.value = {
        id: absence.id,
        employee: absence.employee,
        employeeName: absence.employee.name + ' ' + absence.employee.lastName,
        fromDate: absence.fromDate,
        toDate: absence.toDate,
        absenceType: absence.absenceType,
        observations: absence.observations,
        absenceSupportingDocuments: [...(absence.absenceSupportingDocuments || [])],
        absentWorkTime: absence.absentWorkTime,
      }
      fileList.value = [...(absence.absenceFileList || [])];

      fechaDesde.value = absenceFormModel.value.fromDate;
      manejarCambioDesde(fechaDesde.value);
      fechaHasta.value = absenceFormModel.value.toDate;
      manejarCambioHasta(fechaHasta.value);
    }
  } else {
    fechaDesde.value = startOfDay;
    manejarCambioDesde(fechaDesde.value);
    fechaHasta.value = endOfDay;
    manejarCambioHasta(fechaHasta.value);
  }
  initialSnapshot.value = formSnapshot();
})


onUnmounted(() => {
  v$.value.$reset()
  absenceFormModel.value = {};
  closeDialog();
})

const getAbsenceTypes = (searchQuery = '') => {
  const params = { query: { name: searchQuery } };
  ResourceService.getAbsenceTypes(params)
    .then((res) => {
      absenceTypes.value = res.data['hydra:member'];
    })
}


// Funciones Modal
const removeDocuments = () => {
  newFileList.value.forEach(doc => {
    AbsenceSupportingDocument.deleteAbsenceSupportingDocument(doc.id)
      .then(() => {
        notification.addNotification({
          type: 'success',
          message: 'Justificante eliminado correctamente',
          duration: 6000
        });
      })
      .catch(() => {
        notification.addNotification({
          type: 'error',
          message: 'Error al intentar eliminar el justificante seleccionado.',
          duration: 6000
        });
      })
  });
}

const saveAbsence = async () => {
  if (saving.value) return;
  absenceFormModel.value.fromDate = fechaDesde.value;
  absenceFormModel.value.toDate = fechaHasta.value;
  const valid = await v$.value.$validate();
  const validDates = (
    dayjs(fechaDesde.value).isValid() && dayjs(fechaHasta.value).isValid() &&
    dayjs(fechaHasta.value).isAfter(dayjs(fechaDesde.value))
  );
  if (!valid || !validDates) {
    notification.addNotification({
      type: 'error',
      message: !valid ? 'Complete los campos obligatorios antes de guardar la ausencia.' :
        'Hasta debe ser posterior a Desde.',
      duration: 6000,
    });
    return;
  }

  saving.value = true;
  emits('savingChange', true);
  uiStore.startLoading();
  try {
    // Keep successful uploads for retries when the absence update fails.
    while (pendingFiles.value.length) {
      await saveDocument(pendingFiles.value[0]);
      pendingFiles.value.shift();
    }
    const payload = updateAbsenceFromData({
      ...absenceFormModel.value,
      employee: absenceFormModel.value.employee?.['@id'] || absenceFormModel.value.employee,
      fromDate: dayjs.utc(fechaDesde.value).tz("Europe/Madrid").format(),
      toDate: dayjs.utc(fechaHasta.value).tz("Europe/Madrid").format(),
    });
    if (payload.id != null) {
      await updateAbsence(payload);
    } else {
      await createAbsence(payload);
    }
  } catch {
    notification.addNotification({ type: 'error', message: 'No se han podido guardar los archivos o los cambios. Vuelve a intentarlo.' });
  } finally {
    saving.value = false;
    emits('savingChange', false);
    uiStore.stopLoading();
  }
}

const getEmployees = (filters) => {

  params.value = {
    ...params.value,
    filter: filters,
    'order[name]': 'asc',
    'active': 1,
    page: 1,
    itemsPerPage: 999999
  };
  uiStore.startLoading();
  EmployeeService.getEmployees(params.value)
    .then((res) => {
      employees.value = res.data['hydra:member'];
      totalResults.value = res.data['hydra:totalItems'];
      uiStore.stopLoading();
    })
    .catch(() => {
      uiStore.stopLoading();
    })
}


const formSnapshot = () => JSON.stringify({
  ...absenceFormModel.value,
  fromDate: dayjs(fechaDesde.value).format(),
  toDate: dayjs(fechaHasta.value).format(),
});
const hasUnsavedChanges = computed(() => initialSnapshot.value !== null &&
  (pendingFiles.value.length > 0 || formSnapshot() !== initialSnapshot.value));
const requestClose = () => {
  if (saving.value) return;
  if (hasUnsavedChanges.value) closeWarningShown.value = true;
  else cancelDialog();
};
const cancelDialog = () => {
  if (saving.value) return;
  closeWarningShown.value = false;
  removeDocuments();
  closeDialog();
  emits('closeDialog');
};
const saveAndClose = async () => {
  closeWarningShown.value = false;
  await saveAbsence();
};
// eslint-disable-next-line no-undef
defineExpose({ requestClose });

const closeDialog = () => {
  showModalNewAbsence.value = false;
  deleteDialogShown.value = false;
  selectedAbsence.value = null;
  absenceFormModel.value = {
    id: null,
    employee: null,
    employeeName: '',
    fromDate: startOfDay,
    toDate: endOfDay,
    absenceType: null,
    observations: null,
    absenceSupportingDocuments: []
  };
}

const handleFileUpload = (event) => {
  const newFile = event.target.files?.[0];
  event.target.value = '';
  if (!newFile || saving.value) return;
  if (!/\.(pdf|jpg|jpeg|png|gif|webp|doc|docx)$/i.test(newFile.name) || newFile.size > 5 * 1024 * 1024) {
    notification.addNotification({ type: 'error', message: 'Use ficheros de hasta 5 MB: pdf, jpg, jpeg, png, gif, webp, doc o docx.' });
    return;
  }
  pendingFiles.value.push(newFile);
};
const saveDocument = async (file) => {
  const response = await AbsenceSupportingDocument.uploadDocumentFile(file);
  if (!response?.data?.['@id']) throw new Error('No se ha podido subir el justificante.');
  absenceFormModel.value.absenceSupportingDocuments.push(response.data['@id']);
  fileList.value.push(response.data);
  newFileList.value.push(response.data);
};
const updateAbsence = async (absence) => {
  uiStore.startLoading();
  try {
    const response = await AbsenceService.updateAbsence(absence, absence.id);
    if (!(response?.status >= 200 && response.status < 300)) throw new Error('No se ha actualizado la ausencia.');
    notification.addNotification({ type: 'success', message: 'Ausencia actualizada correctamente' });
    newFileList.value = [];
    emits('changeAbsence');
  } catch {
    notification.addNotification({ type: 'error', message: 'Error al actualizar la ausencia' });
  } finally {
    uiStore.stopLoading();
  }
};
const createAbsence = async (absence) => {
  uiStore.startLoading();
  try {
    const response = await AbsenceService.createAbsence(absence);
    if (!(response?.status >= 200 && response.status < 300)) {
      throw new Error('No se ha creado la ausencia');
    }
    notification.addNotification({
      type: 'success',
      message: 'Ausencia creada correctamente',
    });
    showModalNewAbsence.value = false;
    newFileList.value = [];
    emits('changeAbsence');
  } catch {
    notification.addNotification({ type: 'error', message: 'Error al crear la ausencia' });
  } finally {
    uiStore.stopLoading();
  }
}

const updateAbsenceFromData = (absence) => {
  const { absenceType, ...rest } = absence;
  const updatedAbsence = {
    ...rest, // Any other field
    absenceType: absenceType?.['@id'],
  };
  return updatedAbsence
}

const changeEmployeeModal = (selected) => {
  absenceFormModel.value.employeeName = selected.fullName;
}
const filterEmployeesByName = (query) => {
  employeeSearchQuery.value = query;
}

const requestDocumentRemoval = (document, index, pending = false) => {
  documentRemoval.value = { document, index, pending };
  documentDeleteShown.value = true;
};
const confirmDocumentRemoval = () => {
  if (!documentRemoval.value) return false;
  if (documentRemoval.value.pending) {
    pendingFiles.value.splice(documentRemoval.value.index, 1);
    return true;
  }
  return deleteSupportingDocument(documentRemoval.value.document, documentRemoval.value.index);
};
const deleteSupportingDocument = (document, index) => {
  // uiStore.setLoading(true);
  uiStore.startLoading();
  return AbsenceSupportingDocument.deleteAbsenceSupportingDocument(document.id)
    .then((response) => {
      if (!(response?.status >= 200 && response.status < 300)) throw new Error('No se ha podido eliminar el justificante.');
      notification.addNotification({
        type: 'success',
        message: 'Justificante eliminado correctamente',
        duration: 6000
      });
      let value = fileList.value[index]['@id'];
      fileList.value.splice(index, 1);
      newFileList.value = newFileList.value.filter(item => item.id !== document.id);
      absenceFormModel.value.absenceSupportingDocuments = absenceFormModel.value.absenceSupportingDocuments.filter(e => e !== value);

      // uiStore.setLoading(false);
      uiStore.stopLoading();
    })
    .catch(() => {
      notification.addNotification({
        type: 'error',
        message: 'Error al intentar eliminar el justificante seleccionado.',
        duration: 6000
      });
      // uiStore.setLoading(false);
      uiStore.stopLoading();
      return false;
    })
}

const deshabilitarFechasHasta = (date) => {
  if (fechaDesde.value) {
    const fechaDesdeDate = new Date(fechaDesde.value);
    const dateToCheck = new Date(date);

    // Permitir el mismo día, pero no fechas anteriores
    return (
      dateToCheck.getFullYear() < fechaDesdeDate.getFullYear() ||
      (dateToCheck.getFullYear() === fechaDesdeDate.getFullYear() &&
        dateToCheck.getMonth() < fechaDesdeDate.getMonth()) ||
      (dateToCheck.getFullYear() === fechaDesdeDate.getFullYear() &&
        dateToCheck.getMonth() === fechaDesdeDate.getMonth() &&
        dateToCheck.getDate() < fechaDesdeDate.getDate())
    );
  }
  return false
}
// eslint-disable-next-line no-unused-vars
const deshabilitarFechasDesde = (date) => {
  if (fechaHasta.value) {
    const fechaHastaDate = new Date(fechaHasta.value);
    const dateToCheck = new Date(date);

    // Permitir el mismo día, pero no fechas posteriores
    return (
      dateToCheck.getFullYear() > fechaHastaDate.getFullYear() ||
      (dateToCheck.getFullYear() === fechaHastaDate.getFullYear() &&
        dateToCheck.getMonth() > fechaHastaDate.getMonth()) ||
      (dateToCheck.getFullYear() === fechaHastaDate.getFullYear() &&
        dateToCheck.getMonth() === fechaHastaDate.getMonth() &&
        dateToCheck.getDate() > fechaHastaDate.getDate())
    );
  }
  return false
}

const manejarCambioDesde = (val) => {
  if (fechaHasta.value && val > fechaHasta.value) {
    const fechaDesdeDate = new Date(val);
    const fechaHastaDate = new Date(fechaHasta.value);

    // Si la fecha seleccionada es posterior a la fecha hasta, 
    // reset la fecha hasta
    if (fechaDesdeDate > fechaHastaDate) {
      const temp = new Date(fechaDesdeDate.getFullYear(), fechaDesdeDate.getMonth(), fechaDesdeDate.getDate(), 23, 59);
      fechaHasta.value = temp;
    }
  }
}

const manejarCambioHasta = (val) => {
  if (fechaDesde.value && val < new Date(fechaDesde.value)) {
    const fechaHastaDate = new Date(val);
    const fechaDesdeDate = new Date(fechaDesde.value);

    // Si la fecha seleccionada es anterior a la fecha desde, 
    // reset la fecha desde
    if (fechaHastaDate < fechaDesdeDate) {
      fechaDesde.value = null;
    }
  }
}


const formatAbsentTime = (minutes) => {
  if (minutes === 0) return '0'

  const days = Math.floor(minutes / (24 * 60))
  const hours = Math.floor((minutes % (24 * 60)) / 60)
  const mins = minutes % 60

  let result = ''
  if (days > 0) result += `${days}D:`
  if (hours > 0 || days > 0) result += `${hours.toString().padStart(2, '0')}H:`
  result += `${mins.toString().padStart(2, '0')}M`

  return result.trim()
}


</script>

<template>
  <div class="mt-2 my-body">
    <el-form ref="absenceForm" class="absence-form" :model="absenceFormModel" name="absenceForm" status-icon :disabled="saving"
      @submit.prevent>
      <!-- Nombre del empleado -->
      <div class="form-item">
        <div class="row">
          <div class="col-md-12 col-sm-12 pointer el-col-xs-24">
            <div class="row">
              <div :class="!absenceFormModel.id ? 'col-md-8' : 'col-md-12'" class=" el-col-xs-24">
                <div class="col-md-3 d-inline-flex text-start">
                  <label class="label-absence" for="employeeName">*Nombre</label>
                </div>
                <div class="col-md-8 form-item el-col-xs-24">
                  <el-form-item prop="employeeName">
                    <el-select v-model="absenceFormModel.employee" filterable placeholder="Nombre del empleado"
                      @change="changeEmployeeModal" :filter-method="filterEmployeesByName" :value-key="'@id'">
                      <el-option v-for="employee in filteredEmployees" :key="employee.id" :label="employee.fullName"
                        :value="employee">
                      </el-option>
                    </el-select>
                    <p v-if="v.employeeName.$error" class="invalid-feedback mb-0">
                      <small>
                        {{ v.employeeName.$errors[0].$message }}
                      </small>
                    </p>
                  </el-form-item>
                </div>
              </div>
              <!-- <div v-if="!absenceFormModel.id" class="col-md-4 col-sm-4 text-right el-col-xs-24">
                <button class="btn btn_red btn_new_modal" style="padding-top: 4px" @click="openModalEmployees">
                  Seleccionar Empleado
                </button>
              </div> -->
            </div>
          </div>

        </div>
      </div>
      <!-- Fecha de inicio y de fin -->
      <div class="form-item">
        <div class="row">
          <div class="col-md-12 el-col-xs-24">
            <div class="row">
              <div :class="absenceFormModel.absentWorkTime ? 'col-md-5' : 'col-md-6'" class=" el-col-xs-24">
                <div class="form-item date-picker">
                  <label class="w-100">*Desde:</label>
                  <el-date-picker v-model="fechaDesde" type="datetime" placeholder="Desde" @change="manejarCambioDesde"
                    format="DD/MM/YYYY HH:mm" :prefix-icon="customPrefix" :clearable="false"
                    class="mb-1 date-absence-dialog" />
                  <p v-if="v.fromDate.$error" class="invalid-feedback mb-0">
                    <small>
                      {{ v.fromDate.$errors[0].$message }}
                    </small>
                  </p>
                </div>
              </div>
              <div :class="absenceFormModel.absentWorkTime ? 'col-md-4' : 'col-md-6'" class=" el-col-xs-24">
                <div class="form-item date-picker">
                  <label class="w-100">*Hasta:</label>
                  <el-date-picker v-model="fechaHasta" type="datetime" placeholder="Hasta" @change="manejarCambioHasta"
                    format="DD/MM/YYYY HH:mm" :prefix-icon="customPrefix" :disabled-date="deshabilitarFechasHasta"
                    :clearable="false" class="mb-1  date-absence-dialog" />
                  <p v-if="v.toDate.$error" class="invalid-feedback mb-0">
                    <small>
                      {{ v.toDate.$errors[0].$message }}
                    </small>
                  </p>
                </div>
              </div>
              <div class="col-md-3 align-content-center el-col-xs-24" v-if="absenceFormModel.absentWorkTime">
                <div class="form-item date-picker">
                  <label class="w-100">
                    *Total:
                  </label>
                  <el-input v-model="totalTime" class="mb-1 inputTotal" disabled style="cursor: pointer;">
                  </el-input>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- Tipo de absentismo -->
      <div class="form-item">
        <div class="row">
          <div class="col-md-12">
            <div class="row tex-center mb-3">
              <div class="col-md-8 d-inline-flex el-col-xs-24">
                <label class="label-absence" for="absenceType">*Tipo de absentismo</label>
              </div>
              <div class="col-md-8 form-item el-col-xs-24">
                <el-select v-model="absenceFormModel.absenceType" placeholder="Tipo de Ausencia" :value-key="'@id'"
                  class="date-absence-dialog">
                  <el-option v-for="item in absenceTypes" :key="item.id" :label="item.name" :value="item" />
                </el-select>
                <p v-if="v.absenceType.$error" class="invalid-feedback mb-0">
                  <small>
                    {{ v.absenceType.$errors[0].$message }}
                  </small>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- Observaciones -->
      <div class="form-item">
        <div class="row text-center">
          <div class="col-md-12 el-col-xs-24">
            <div class="row tex-center">
              <div class="col-md-3 d-inline-flex el-col-xs-24">
                <label class="label-absence label-observations">*Observaciones</label>
              </div>
              <div class="col-md-12 el-col-xs-24">
                <el-form-item prop="observations">
                  <textarea v-model="absenceFormModel.observations" class="font18 w-100 p-2" style="height: 200px;"
                    placeholder="Observaciones del motivo de la baja" />
                  <small v-if="v.observations.$error" class="invalid-feedback mb-0">
                    {{ v.observations.$errors[0].$message }}
                  </small>
                </el-form-item>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- Justificantes -->
      <section v-if="absenceFormModel.id != null" class="absence-documents">
        <AbsenceDocumentPicker id="absence-justification-file" class="mb-3" :disabled="saving"
          @change="handleFileUpload" />
        <p v-if="!fileList.length && !pendingFiles.length" class="absence-documents__empty">Sin justificantes</p>
        <div v-for="(document, index) in fileList" :key="document['@id'] || document.id" class="mb-3">
          <AbsenceDocumentAttachment :attachment="document" :disabled="saving"
            @remove="requestDocumentRemoval(document, index)" />
        </div>
        <div v-for="(file, index) in pendingFiles" :key="file.name + '-' + file.lastModified + '-' + index" class="mb-3">
          <AbsenceDocumentAttachment :attachment="file" file :disabled="saving"
            @remove="requestDocumentRemoval(file, index, true)" />
        </div>
      </section>
      <!-- Botones Cancelar y Nuevo/Guardar -->
      <div class="row mt-4">
        <div class="col d-flex justify-content-end gap-3 button-absence-container">
          <button type="button" class="btn btn_yellow px-4 button-absence" :disabled="saving" @click="requestClose">Cerrar</button>

          <button class="btn btn_yellow px-4 button-absence" :disabled="saving" @click="saveAbsence()">
            Guardar
          </button>
        </div>
      </div>
    </el-form>
    <SharedDeleteConfirmDialog v-model="documentDeleteShown" title="Eliminar justificante"
      message="¿Desea eliminar este justificante?" :confirm-action="confirmDocumentRemoval"
      @cancel="documentRemoval = null" />
    <SharedDeleteConfirmDialog v-model="closeWarningShown" title="Eliminar cambios sin guardar"
      message="Hay archivos y/o cambios que aún no se han guardado."
      secondary-message="¿Quieres subir los archivos y guardar los cambios antes de cerrar?"
      confirm-text="Cerrar sin guardar" cancel-text="Seguir editando" :width="680" :loading="saving"
      :show-title-icon="false" @confirm="cancelDialog">
      <template #extra-action="{ busy }">
        <button type="button" class="btn btn_green px-4" :disabled="busy" @click="saveAndClose">Guardar y cerrar</button>
      </template>
    </SharedDeleteConfirmDialog>
  </div>
</template>

<style lang="scss">
.my-body {
  margin-right: 5%;
}


.btn_new_modal {
  min-height: 40px;
  margin-top: 10%;
  margin-left: -2%;
}

.absences {
  .label-absence {
    color: #434343;
    float: left;
    display: block;
    white-space: nowrap;

  }
}

.inputTotal {
  .el-input__wrapper {
    .el-input__inner {
      cursor: default;
      -webkit-text-fill-color: #434343;
    }
  }
}

@media(min-width: 768px) {
  .button-absence {
    width: 25%;
  }
}

@media (max-width:767px) {
  .my-header {
    #asterisk {
      display: none;
    }

    .text-header {
      margin-top: 0px !important;
      font-size: 2em !important;
    }
  }

  .button-absence-container {
    justify-content: center !important;
    margin-left: 10px;
    margin-right: -10px;
  }

  .button-absence {
    width: 50%;
  }

  .date-absence-dialog {
    width: 100% !important;
  }
}
</style>

<style scoped lang="scss">
.absence-documents {
  width: 100%;
  min-width: 0;
  &__empty { padding: 12px 16px; border: 1px solid #dcdfe6; border-radius: 20px; background: #f5f7fa; color: #73767a; }
}
</style>
